import React, { useState } from "react";
import ScrollModal from "@/components/common/scroll-modal";
import SelectField from "@/components/common/select";
import {
  Heading,
  Button,
  Badge,
  Avatar,
  AvatarFallback,
  Icon,
  AvatarImage,
  FileItem,
} from "@elaniintc/goes-design-system";
import TextField, { TextAreaField } from "@/components/common/text-field";
import {
  uploadFile,
  useCreateProcess,
  useAssignTagToProcess,
} from "@/queries/process/process.api";
import { useGetTags } from "@/queries/tags/tags.api";
import type { AxiosError } from "axios";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { newProcessSchema } from "@/schemas/processes";
import type { StoreProcessData } from "@/types/entities/process";

const MAX_FILE_SIZE_MB = 25; //Number(getEnv('VITE_MAX_FILE_SIZE_MB') || 20); // 20 MB por defecto
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

interface NewProcessModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const initialState = {
  name: "",
  tag_id: "",
  description: "",
  files: [] as File[],
};

interface InvalidFile {
  name: string;
  size: number;
  reason?: "muy_pesado" | "tipo_no_permitido";
}

// Tipo de retorno del hook
interface UseFileUploadReturn {
  files: File[];
  setFiles: React.Dispatch<React.SetStateAction<File[]>>;
  invalidFiles: InvalidFile[];
  setInvalidFiles: React.Dispatch<React.SetStateAction<InvalidFile[]>>;
  isDragging: boolean;
  setIsDragging: React.Dispatch<React.SetStateAction<boolean>>;
  handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => void;
  handleRemoveFile: (name: string) => void;
}

const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "image/png",
  "image/jpeg",
  "application/zip",
  "application/x-zip-compressed",
];

// Custom hook for file upload logic
// eslint-disable-next-line react-refresh/only-export-components
export function useFileUpload(
  maxFileSizeBytes: number,
  cb: (files: File[]) => void,
): UseFileUploadReturn {
  const [files, setFiles] = useState<File[]>([]);
  const [invalidFiles, setInvalidFiles] = useState<InvalidFile[]>([]);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const addFiles = (fileList: File[]): void => {
    // Only take the first file
    const file = fileList[0];
    if (!file) return;

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setInvalidFiles([
        {
          name: file.name,
          size: file.size,
          reason: "tipo_no_permitido",
        },
      ]);
      setFiles([]);
      cb([]);
      return;
    }

    if (file.size > maxFileSizeBytes) {
      setInvalidFiles([
        {
          name: file.name,
          size: file.size,
          reason: "muy_pesado",
        },
      ]);
      setFiles([]);
      cb([]);
      return;
    }

    setFiles([file]);
    cb([file]);
    setInvalidFiles([]);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const fileList = Array.from(e.target.files || []);
    addFiles(fileList);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const fileList = Array.from(e.dataTransfer.files || []);
    addFiles(fileList);
  };

  const handleRemoveFile = (name: string): void => {
    const newFiles = files.filter((file) => file.name !== name);
    setFiles(newFiles);
    cb(newFiles);
    setInvalidFiles((prev) => prev.filter((file) => file.name !== name));
  };

  return {
    files,
    setFiles,
    invalidFiles,
    setInvalidFiles,
    isDragging,
    setIsDragging,
    handleFileChange,
    handleDrop,
    handleRemoveFile,
  };
}

export default function NewProcessModal({
  open,
  onClose,
  onSuccess,
}: NewProcessModalProps) {
  const { mutateAsync, isPending: isLoading } = useCreateProcess();
  const { mutateAsync: assignTagMutation } = useAssignTagToProcess();

  const { data: tagsData, isLoading: isLoadingTags } = useGetTags({
    order_by: "!updated_at",
    per_page: 1000,
    relationships: "files",
  });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    watch,
    trigger,
  } = useForm({
    resolver: zodResolver(newProcessSchema),
    mode: "onChange",
    reValidateMode: "onChange",
    defaultValues: initialState,
  });
  const [description, tagId] = watch(["description", "tag_id"]);
  const [isLoadingFiles, setIsLoadingFiles] = useState(false);

  // Use custom hook for file logic
  const {
    files,
    setFiles,
    invalidFiles,
    isDragging,
    setIsDragging,
    handleFileChange,
    handleDrop,
    handleRemoveFile,
  } = useFileUpload(MAX_FILE_SIZE_BYTES, (files) => {
    setValue("files", files);
  });

  const onSubmit = async (data: StoreProcessData) => {
    try {
      const process = await mutateAsync({
        name: data.name,
        description: data.description,
      });
      const processId = process.data.id;
      if (!processId) {
        setError("root", {
          type: "name",
          message: "No se pudo crear el proceso. Intenta nuevamente.",
        });
        return;
      }

      // Subir archivos si los hay
      if (files.length > 0) {
        await Promise.all(
          files.map((file) =>
            uploadFile({
              file,
              entity_id: processId,
              entity_type: "process",
            }),
          ),
        );
      }

      // Asignar tag si se seleccionó uno
      if (data.tag_id) {
        await assignTagMutation({
          tag_id: data.tag_id,
          entity: "process",
          entity_id: processId,
        });
      }

      clearErrors();
      handleClose();
      onSuccess();
    } catch (e) {
      const error = e as AxiosError<{
        message?: string;
        errors?: { [key: string]: string[] };
      }>;

      // Manejo de error por nombre duplicado
      const status = error?.response?.status;
      const apiMessage = error?.response?.data?.message?.toLowerCase?.() ?? "";
      const nameErrorMsg =
        error?.response?.data?.errors?.name?.[0]?.toLowerCase?.() ?? "";
      const combinedMsg = apiMessage + nameErrorMsg;

      if (
        combinedMsg.includes("validation.unique") ||
        combinedMsg.includes("name has already been taken") ||
        combinedMsg.includes("ya ha sido tomado") ||
        combinedMsg.includes("ya está en uso") ||
        (status === 422 && nameErrorMsg.length > 0)
      ) {
        setError("name", {
          type: "duplicate",
          message: "El nombre del proceso ya está en uso. Por favor elige otro.",
        });
      } else {
        setError("name", {
          type: "server",
          message:
            error?.response?.data?.message ||
            "Error al crear el proceso. Intenta nuevamente.",
        });
      }
      // Aquí podrías manejar errores específicos si es necesario
    } finally {
      setIsLoadingFiles(false);
    }
  };

  const handleClose = () => {
    reset({
      name: "",
      tag_id: "",
      description: "",
    });
    setValue("files", []);
    setFiles([]);
    onClose();
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] flex flex-col"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <Heading variant="h5" className="!mb-0">
            Nuevo Proceso
          </Heading>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" className="text-xl text-gray-500" />
          </Button>
        </div>
      }
      footer={
        <div className="w-full grid grid-cols-1 tablet:grid-cols-2 py-3 px-8 gap-3">
          <Button
            variant="tertiary"
            size="sm"
            type="button"
            onClick={handleClose}
            className="min-w-[120px] cursor-pointer"
          >
            Cancelar
          </Button>
          <Button
            variant="default"
            size="sm"
            type="submit"
            className="min-w-[140px] cursor-pointer disabled:bg-neutral-object-accent disabled:text-on-disabled disabled:border-none disabled:shadow-none"
            disabled={isLoadingFiles || !isValid || isLoading}
            form="new-process-form"
          >
            {isLoadingFiles || isLoading ? "Creando..." : "Crear proceso"}
          </Button>
        </div>
      }
    >
      <form
        id="new-process-form"
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col flex-1 min-h-0 px-4 sm:px-8"
      >
        {/* Contenido scrollable */}
        <div className="flex flex-col gap-6 pb-6">
          {/* Detalles */}
          <div>
            <Heading variant="h6" className="mb-4">
              Detalles
            </Heading>
            <div className="flex flex-col gap-4">
              <TextField
                label="Nombre"
                required
                maxLength={100}
                error={errors.name?.message || ""}
                placeholder="Nombre del proceso"
                {...register("name")}
              />
              <SelectField
                name="tag_id"
                label="Etiqueta"
                required
                optionTextClassName="[&>*]:block [&>*]:w-full"
                placeholder="Elegir una etiqueta"
                error={errors.tag_id?.message || ""}
                value={tagId || ""}
                onChange={(value) => {
                  setValue("tag_id", value);
                  setTimeout(() => {
                    trigger("tag_id");
                  }, 0);
                }}
                isLoading={isLoadingTags}
                options={(() => {
                  const options = tagsData?.data.map((tag) => ({
                    label: tag.name,
                    value: tag.id.toString(),
                    avatar: (
                      <Avatar size="2xs" shape="square">
                        <AvatarImage src={tag?.files?.[0]?.url} />
                        <AvatarFallback color="neutral" shape="square">
                          <Icon name="tag" type="fas" />
                        </AvatarFallback>
                      </Avatar>
                    ),
                    iconPosition: "right",
                    icon: (
                      <Badge
                        size="sm"
                        shape="pill"
                        variant={tag.active ? "success" : "neutral"}
                      >
                        {tag.active ? "Activa" : "Inactiva"}
                      </Badge>
                    ),
                  }));
                  return options || [];
                })()}
              />
              <TextAreaField
                label="Descripción"
                required
                maxLength={500}
                placeholder="Describe este proceso..."
                rows={3}
                className="resize-none"
                error={errors.description?.message || ""}
                {...register("description")}
                count={description?.length || 0}
                limit={500}
              />
            </div>
          </div>

          <hr className="border-t border-neutral" />

          {/* Carga de fichas técnicas */}
          <div>
            <Heading variant="h5" className="mb-2">
              Carga de fichas técnicas
            </Heading>
            <div
              className={`border border-dashed rounded-xl p-4 flex flex-col items-center justify-center gap-2 transition-colors duration-200 ${
                isDragging
                  ? "border-neutral bg-surface-action-accent-quiet"
                  : "border-neutral"
              }`}
              style={{ minHeight: 120 }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDragging(false);
              }}
              onDrop={handleDrop}
            >
              {isDragging ? (
                <div className="flex flex-col items-center justify-center gap-2">
                  <Avatar shape="square">
                    <AvatarFallback color="action" shape="square">
                      <Icon
                        type="far"
                        name="arrow-up"
                        className="text-base text-action-quiet mb-1 w-3"
                      />
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-action-quiet font-medium">
                    Suelta tu archivo aquí
                  </span>
                </div>
              ) : (
                <>
                  <span className="text-body text-sm">
                    Arrastra y Suelta o{" "}
                    <label className="text-action cursor-pointer font-medium">
                      Selecciona un archivo
                      <input
                        type="file"
                        accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.zip"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                    </label>
                  </span>
                  <span className="text-xs text-body-quiet">
                    Sube un archivo (.pdf, .docx, .xlsx, .png, .jpg, .jpeg,
                    .zip)
                  </span>
                </>
              )}
              {errors.files?.message && (
                <span className="text-danger text-xs">
                  {errors.files?.message || ""}
                </span>
              )}
            </div>

            {/* Lista de archivos subidos y archivos inválidos fuera de la dropzone */}
            {(files.length > 0 || invalidFiles.length > 0) && (
              <div className="mt-4 flex flex-col gap-3">
                {/* Archivos válidos */}
                {files.map((file) => (
                  <FileItem
                    fileName={file.name}
                    tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                    color="neutral"
                    action={<Icon type="far" name="trash" size="sm" />}
                    actionAriaLabel="Borrar"
                    onAction={() => {
                      handleRemoveFile(file.name);
                    }}
                  />
                ))}
                {/* Archivos inválidos que no están en la lista de válidos */}
                {invalidFiles
                  .filter((f) => !files.some((file) => file.name === f.name))
                  .map((file, index) => (
                    <div
                      className="flex flex-col gap-1.5"
                      key={`${file.name}-error-${index}`}
                    >
                      <FileItem
                        key={file.name}
                        fileName={file.name}
                        tag={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                        color="danger"
                        action={<Icon type="far" name="trash" size="sm" />}
                        actionAriaLabel="Borrar"
                        onAction={() => handleRemoveFile(file.name)}
                      />
                      <span className="text-xs text-danger mt-0 mb-2 flex items-center gap-1 pl-2">
                        <Icon
                          type="far"
                          name="circle-xmark"
                          className="text-danger text-sm"
                        />
                        Archivo muy pesado, no se incluirá en el proceso.
                      </span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </form>
    </ScrollModal>
  );
}
